import React, { useEffect } from 'react';
import { BrowserRouter } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { useDispatch } from 'react-redux';
import theme from './theme';

// Routes
import { AppRoutes } from './routes';

// Auth
import { getCurrentUser, logout } from './redux/slices/authSlice';
import { getToken } from './utils/storageUtils';
import { isTokenExpired } from './utils/tokenUtils';

/**
 * App - Main application component
 * 
 * CRITICAL: This project uses React Router 5!
 * - Use <Switch> instead of <Routes>
 * - Use component/render prop instead of element prop
 * - Use useHistory instead of useNavigate
 * 
 * All routes are defined in src/routes/index.jsx for better organization.
 * 
 * AUTH INITIALIZATION:
 * - Centralized auth check happens here on app mount
 * - Validates token and restores user state
 * - Eliminates redundant checks in components/routes
 */
function App() {
  const dispatch = useDispatch();

  // 🔐 CENTRALIZED AUTH INITIALIZATION
  // Runs once on app mount - validates token and restores user state
  useEffect(() => {
    const initializeAuth = async () => {
      const token = getToken();

      // No token → clear auth state
      if (!token) {
        dispatch(logout());
        return;
      }

      // Token expired → logout and clear state
      if (isTokenExpired(token)) {
        dispatch(logout());
        return;
      }

      // Token valid → fetch user data
      try {
        await dispatch(getCurrentUser()).unwrap();
        // ✅ Success: user data now in Redux, isAuthenticated = true
      } catch (error) {
        // ❌ API error (401, 403, invalid token, etc.) → logout
        console.error('Auth initialization failed:', error);
        dispatch(logout());
      }
    };

    initializeAuth();
  }, [dispatch]); // Only run once on mount

  // Optional: Show loading screen during initialization
  // Uncomment if you want a global loading screen
  // if (loading) {
  //   return (
  //     <ThemeProvider theme={theme}>
  //       <CssBaseline />
  //       <Box display="flex" alignItems="center" justifyContent="center" minHeight="100vh">
  //         <CircularProgress size={60} />
  //       </Box>
  //     </ThemeProvider>
  //   );
  // }

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <BrowserRouter>
        <AppRoutes />
      </BrowserRouter>
    </ThemeProvider>
  );
}

export default App;
